import type { CardType } from '../../../samba-payment.types';
import type { LosslessNumber } from '../../../lossless-json-codec';
import { CloverGatewayId } from './clover-system-constants';

export const CloverTransactionDetailsOutcome = {
    FOUND: 'found',
    MISSING: 'missing',
    MALFORMED_RESPONSE: 'malformed_response',
} as const;

export type CloverTransactionDetailsOutcome =
    (typeof CloverTransactionDetailsOutcome)[keyof typeof CloverTransactionDetailsOutcome];

export const CloverGatewayTransactionDetailsType = 'clover_gateway_transaction_details' as const;

export const CloverTransactionDetailsPaymentResult = {
    SUCCESS: 'SUCCESS',
    FAIL: 'FAIL',
    INITIATED: 'INITIATED',
    VOIDED: 'VOIDED',
    VOIDING: 'VOIDING',
    VOID_FAILED: 'VOID_FAILED',
    AUTH: 'AUTH',
    AUTH_COMPLETED: 'AUTH_COMPLETED',
    DISCOUNT: 'DISCOUNT',
} as const;

export type CloverTransactionDetailsPaymentResult =
    (typeof CloverTransactionDetailsPaymentResult)[keyof typeof CloverTransactionDetailsPaymentResult];

export interface CloverTransactionDetailsRequestHeaders {
    readonly deviceId: string;
    readonly posId: string;
    readonly userAgent: string;
    readonly timeoutSeconds?: number;
}

export interface CloverPlatformTransactionDetailsRequestHeaders {
    readonly userAgent: string;
}

export interface CloverTransactionDetailsRequestDetails {
    readonly method: 'GET';
    readonly path: '/v3/merchants/{merchantId}/payments/{paymentId}';
    readonly merchantId: string;
    readonly paymentId: string;
    readonly expand: 'additionalCharges,refunds,cardTransaction,tender';
    readonly headers: CloverPlatformTransactionDetailsRequestHeaders;
}

export interface CloverTransactionDetailsCardTransactionBody {
    readonly authCode?: string;
    readonly cardType?: string;
    readonly cardholderName?: string;
    readonly last4?: string;
    readonly referenceId?: string;
    readonly state?: string;
    readonly token?: string;
    readonly transactionNo?: string;
    readonly type?: string;
}

export interface CloverTransactionDetailsTenderBody {
    readonly id?: string;
    readonly label?: string;
    readonly labelKey?: string;
}

export interface CloverTransactionDetailsRefundBody {
    readonly id?: string;
    readonly amount?: number;
    readonly createdTime?: number;
    readonly externalReferenceId?: string;
    readonly payment?: {
        readonly id?: string;
    };
    readonly taxAmount?: number;
    readonly tipAmount?: number;
    readonly voided?: boolean;
}

export interface CloverTransactionDetailsAdditionalChargeBody {
    readonly id?: string;
    readonly amount: number;
    readonly rate?: number;
    readonly type: string;
    readonly payment?: {
        readonly id: string;
    };
    readonly refund?: {
        readonly id: string;
    };
}

export interface CloverTransactionDetailsAdditionalChargesBody {
    readonly elements: readonly CloverTransactionDetailsAdditionalChargeBody[];
}

export interface CloverTransactionDetailsRefundsBody {
    readonly elements: readonly CloverTransactionDetailsRefundBody[];
}

export interface CloverTransactionDetailsPaymentBody {
    readonly id: string;
    readonly amount?: number;
    readonly result?: CloverTransactionDetailsPaymentResult;
    readonly cardTransaction?: CloverTransactionDetailsCardTransactionBody;
    readonly createdTime?: number;
    readonly externalPaymentId?: string;
    readonly externalReferenceId?: string;
    readonly offline?: boolean;
    readonly refunds?:
        | readonly CloverTransactionDetailsRefundBody[]
        | CloverTransactionDetailsRefundsBody;
    readonly additionalCharges?: CloverTransactionDetailsAdditionalChargesBody;
    readonly taxAmount?: number;
    readonly tipAmount?: number;
    readonly tender?: CloverTransactionDetailsTenderBody;
    readonly voided?: boolean;
}

export interface CloverTransactionStatusPaymentResponse {
    readonly payment: CloverTransactionDetailsPaymentBody;
}

interface CloverNormalizedGatewayTransactionDetailsBase {
    readonly gateway_id: typeof CloverGatewayId;
    readonly gateway_transaction_details_type: typeof CloverGatewayTransactionDetailsType;
    readonly gateway_txn_id: string;
    readonly base_amount: LosslessNumber;
    readonly total_amount: LosslessNumber;
    readonly total_fee_amount: LosslessNumber;
    readonly total_tax_amount: LosslessNumber;
    readonly is_void: boolean;
    readonly is_settled: boolean;
    readonly refund_amount: LosslessNumber;
    readonly refund_count: number;
    readonly card_brand?: string;
    readonly card_last4?: string;
    readonly cardholder_name?: string;
    readonly card_type?: CardType;
    readonly settlement_status?: string;
    readonly authorization_code?: string;
    readonly batch_id?: string;
}

export interface CloverNormalizedGatewayTransactionDetailsV1 extends CloverNormalizedGatewayTransactionDetailsBase {
    readonly gateway_transaction_details_version: 1;
}

export interface CloverNormalizedAdditionalCharge {
    readonly provider_id?: string;
    readonly amount: LosslessNumber;
    readonly rate?: LosslessNumber;
    readonly type: string;
    readonly payment_id?: string;
    readonly refund_id?: string;
}

export interface CloverNormalizedGatewayTransactionDetailsV2 extends CloverNormalizedGatewayTransactionDetailsBase {
    readonly gateway_transaction_details_version: 2;
    readonly credit_surcharge_amount: LosslessNumber;
    readonly additional_charges: readonly CloverNormalizedAdditionalCharge[];
}

export interface CloverNormalizedGatewayTransactionDetails extends CloverNormalizedGatewayTransactionDetailsBase {
    readonly gateway_transaction_details_version: 2;
    readonly credit_surcharge_amount: LosslessNumber | null;
    readonly additional_charges: readonly CloverNormalizedAdditionalCharge[] | null;
}

export type CloverTransactionDetailsResponseDetails =
    | {
          readonly outcome: typeof CloverTransactionDetailsOutcome.FOUND;
          readonly httpStatus: 200;
          readonly rawDetails: CloverTransactionDetailsPaymentBody;
          readonly normalizedDetails: CloverNormalizedGatewayTransactionDetails;
      }
    | {
          readonly outcome: typeof CloverTransactionDetailsOutcome.MISSING;
          readonly httpStatus: number;
          readonly rawDetails: unknown;
          readonly message: string;
      }
    | {
          readonly outcome: typeof CloverTransactionDetailsOutcome.MALFORMED_RESPONSE;
          readonly httpStatus: number;
          readonly rawDetails: unknown;
          readonly message: string;
      };
